'use client';

import * as React from 'react';
import {
  AccordionItem as ShadcnAccordionItem,
  AccordionTrigger as ShadcnAccordionTrigger,
  AccordionContent as ShadcnAccordionContent,
  Accordion as ShadcnAccordion,
  type AccordionTriggerDensity,
  type AccordionChevronSize,
  type AccordionTriggerPosition,
  type AccordionSpaceStep,
} from '@/hen-ui/shadcn/accordion';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn, resolveAnchorId, warnOnce } from '@/hen-ui/utils';
import { SPACE_GAP, SPACE_PX } from '@/hen-ui/utils/space-steps';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import { Flex } from '@/hen-ui/composites/Flex';
import {
  accordionVariants,
  type AccordionVariantProps,
} from './accordion.variants';

// Optional, backward-compatible presentation knobs — all default to current rendering.
// Set on `Accordion` for every item, or on an `AccordionItem` to override one item.
type AccordionTitleSize = 'h3' | 'h4' | 'h5' | 'h6';
type AccordionTitleWeight = 'regular' | 'medium' | 'semibold' | 'bold';
type AccordionTriggerIconSize = 'sm' | 'sm-md' | 'md';
type AccordionChevronSizeOption = AccordionChevronSize | 'follow-title';

const TITLE_WEIGHT_CLASS: Record<AccordionTitleWeight, string> = {
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

interface AccordionStyleProps {
  /** Trigger density. `compact` tightens vertical padding and the default chevron. Default `default`. */
  density?: AccordionTriggerDensity;
  /** Trigger title size. An accordion title is always a semantic heading. Default `h5`. */
  titleSize?: AccordionTitleSize;
  /** Trigger title weight. Default `bold`. Label-slot text inherits this. */
  titleWeight?: AccordionTitleWeight;
  /** Leading icon size. Default `sm`. */
  iconSize?: AccordionTriggerIconSize;
  /** Chevron size, or `follow-title` to scale with the title. Default `xl` (`lg` at compact density). */
  chevronSize?: AccordionChevronSizeOption;
  /** Corner radius on the `white` / `neutral` variants. Default `sm`. */
  radius?: AccordionVariantProps['radius'];
  /** Where the title and arrow sit in the section header. Spread is the usual layout. Start or end keeps them together on one side. Default `spread`. */
  triggerPosition?: AccordionTriggerPosition;
  /** Horizontal padding on the section header. Overrides density on that axis when set. */
  triggerPaddingX?: AccordionSpaceStep;
  /** Vertical padding on the section header. Overrides density on that axis when set. */
  triggerPaddingY?: AccordionSpaceStep;
  /** Horizontal padding on the open section body. Also aligns always-visible content. */
  contentPaddingX?: AccordionSpaceStep;
  /** Bottom padding on the open section body. Top stays flush with the header. */
  contentPaddingY?: AccordionSpaceStep;
  /** Space between the icon, labels, and title in the header. Default `xs`. */
  labelGap?: AccordionSpaceStep;
}

// Chevron sizes when `chevronSize='follow-title'` is chosen (opt-in; the
// default keeps the original `xl` regardless of title size).
const FOLLOW_TITLE_CHEVRON: Record<AccordionTitleSize, AccordionChevronSize> = {
  h3: 'xl',
  h4: 'xl',
  h5: 'lg',
  h6: 'md',
};

// Lets `AccordionItem` inherit the parent `Accordion`'s visual variant without
// the author repeating it on every item (an item may still override it).
const AccordionVariantContext = React.createContext<
  AccordionVariantProps['variant'] | undefined
>(undefined);

// Carries the parent `Accordion`'s presentation knobs to each `AccordionItem`,
// mirroring the variant context. Per-item props override these.
const AccordionConfigContext = React.createContext<
  AccordionStyleProps | undefined
>(undefined);

interface AccordionItemData extends AccordionStyleProps {
  title?: string;
  content: React.ReactNode;
  icon?: React.ComponentProps<typeof Icon>['icon'];
  iconBg?: boolean;
  value?: string;
  beforeLabel?: React.ReactNode;
  afterLabel?: React.ReactNode;
  aboveTitle?: React.ReactNode;
  belowTitle?: React.ReactNode;
  supportingContent?: React.ReactNode;
  trailingContent?: React.ReactNode;
}

// Base props shared by the programmatic (`items`) accordion types
interface BaseAccordionProps
  extends AccordionVariantProps,
    AccordionStyleProps {
  items: AccordionItemData[];
}

// Single accordion can have collapsible
interface SingleAccordionProps extends BaseAccordionProps {
  type?: 'single';
  collapsible?: boolean;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children?: never;
}

// Multiple accordion cannot have collapsible
interface MultipleAccordionProps extends BaseAccordionProps {
  type: 'multiple';
  collapsible?: never;
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  children?: never;
}

// Compound (DSL-authorable) mode: compose `AccordionItem` children instead of
// the programmatic `items` array. Each `AccordionItem` owns its trigger
// (`title` and optional `beforeLabel` / `afterLabel` slots), optional
// `supportingContent` and `trailingContent` that stay visible, and a real
// `children` body slot, so a config can author one or more collapsible
// sections with rich content (the
// DSL runtime only renders a top-level array-of-nodes slot). Additive &
// non-breaking — `items` callers are unchanged. Split by `type` (mirroring the
// items variants) so single mode can't be paired with a multiple-mode
// `value`/`onValueChange`, and vice versa.
interface CompoundSingleAccordionProps
  extends AccordionVariantProps,
    AccordionStyleProps {
  type?: 'single';
  collapsible?: boolean;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  /** `AccordionItem` children. */
  children: React.ReactNode;
  items?: never;
}

interface CompoundMultipleAccordionProps
  extends AccordionVariantProps,
    AccordionStyleProps {
  type: 'multiple';
  collapsible?: never;
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  /** `AccordionItem` children. */
  children: React.ReactNode;
  items?: never;
}

// Discriminated union. `anchorId` is the in-page jump target rendered as the root `id`.
type AccordionAnchorProps = {
  /**
   * In-page jump target. Rendered as the root element's `id`.
   * A leading `#` is ignored. Blank values are omitted.
   */
  anchorId?: string;
};

export type AccordionProps = (
  | SingleAccordionProps
  | MultipleAccordionProps
  | CompoundSingleAccordionProps
  | CompoundMultipleAccordionProps
) &
  AccordionAnchorProps;

const isItemsMode = (
  props: AccordionProps,
): props is SingleAccordionProps | MultipleAccordionProps =>
  !('children' in props && props.children != null) &&
  'items' in props &&
  Array.isArray(props.items) &&
  props.items.length > 0;

const toSingleValue = (value: unknown): string | undefined =>
  typeof value === 'string' ? value.trim() : undefined;

const toSingleOpenValue = (value: unknown): string | undefined => {
  const normalized = toSingleValue(value);
  return normalized ? normalized : undefined;
};

const toMultipleValue = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
};

const toMultipleOpenValue = (value: unknown): string[] | undefined => {
  const normalized = toMultipleValue(value);
  return normalized && normalized.length > 0 ? normalized : undefined;
};

type AccordionRootProps =
  | {
      type: 'single';
      collapsible: boolean;
      defaultValue?: string;
      value?: string;
      onValueChange?: (value: string) => void;
    }
  | {
      type: 'multiple';
      defaultValue?: string[];
      value?: string[];
      onValueChange?: (value: string[]) => void;
    };

const resolveAccordionRootProps = (
  props: AccordionProps,
): AccordionRootProps => {
  const hasChangeHandler = typeof props.onValueChange === 'function';
  const type = props.type ?? 'single';

  if (type === 'single') {
    const authoredDefault = toSingleOpenValue(props.defaultValue);
    const authoredValue = toSingleOpenValue(props.value);
    const controlledValue = toSingleValue(props.value);
    const isControlled = hasChangeHandler && controlledValue !== undefined;
    const defaultValue =
      authoredDefault ?? (isControlled ? undefined : authoredValue);

    return {
      type: 'single',
      collapsible: props.collapsible ?? true,
      ...(defaultValue !== undefined ? { defaultValue } : {}),
      ...(hasChangeHandler
        ? {
            onValueChange: props.onValueChange as (value: string) => void,
            ...(isControlled ? { value: controlledValue } : {}),
          }
        : {}),
    };
  }

  const authoredDefault = toMultipleOpenValue(props.defaultValue);
  const authoredValue = toMultipleOpenValue(props.value);
  const controlledValue = toMultipleValue(props.value);
  const isControlled = hasChangeHandler && controlledValue !== undefined;
  const defaultValue =
    authoredDefault ?? (isControlled ? undefined : authoredValue);

  return {
    type: 'multiple',
    ...(defaultValue !== undefined ? { defaultValue } : {}),
    ...(hasChangeHandler
      ? {
          onValueChange: props.onValueChange as (value: string[]) => void,
          ...(isControlled ? { value: controlledValue } : {}),
        }
      : {}),
  };
};

const collectTriggerText = (node: React.ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(collectTriggerText).filter(Boolean).join(' ');
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return collectTriggerText(node.props.children);
  }
  return '';
};

// Keep label-slot markup as phrasing content: coerce direct `Text` block tags
// to `span`. Do not walk nested trees or wrap `render` props — that turns a
// single child into an array and breaks Radix `asChild` / Slot and
// function-as-children. Nested label-slot Text inherits the heading's
// `titleWeight` (Puck always sets a catalog default weight, so treating
// `weight != null` as "authored" would pin every DSL slot to regular).
const asInlineTriggerContent = (node: React.ReactNode): React.ReactNode =>
  React.Children.map(node, (child) => {
    if (!React.isValidElement<{ tag?: string }>(child)) return child;
    if (child.type !== Text) return child;
    const tag = child.props.tag;
    if (tag != null && tag !== 'p' && tag !== 'div') return child;
    return React.cloneElement(child, { tag: 'span' });
  });

const insetRegion = (
  content: React.ReactNode,
  contentPaddingX?: AccordionSpaceStep,
) =>
  content ? (
    <>
      {/* hen-ui-allow-raw-html -- inset wrapper, not a layout */}
      <div
        className={
          contentPaddingX != null ? SPACE_PX[contentPaddingX] : 'px-sm'
        }
      >
        {content}
      </div>
    </>
  ) : null;

export interface AccordionItemProps
  extends AccordionVariantProps,
    AccordionStyleProps {
  /** Trigger label for this section. Optional when slot content names the trigger. */
  title?: string;
  /** Section body content (children slot). */
  children?: React.ReactNode;
  /** Optional leading icon on the trigger. Stays on the title row when the title is stacked. */
  icon?: React.ComponentProps<typeof Icon>['icon'];
  /** Render the icon inside a colored circle. Defaults to `true`. */
  iconBg?: boolean;
  /**
   * Inline content shown before the section heading. Becomes a leading column
   * beside the stacked lines when `aboveTitle` or `belowTitle` is set. The
   * `icon` prop stays on the title row.
   */
  beforeLabel?: React.ReactNode;
  /** Inline content shown after the section heading. */
  afterLabel?: React.ReactNode;
  /** Line shown above the section heading, inside the trigger. */
  aboveTitle?: React.ReactNode;
  /** Line shown below the section heading, inside the trigger. */
  belowTitle?: React.ReactNode;
  /** Content that stays visible while the section content collapses. */
  supportingContent?: React.ReactNode;
  /** Content that stays visible below the collapsible section content. */
  trailingContent?: React.ReactNode;
  /** Stable value for deep-linking / default-open. Auto-generated when unset. */
  value?: string;
}

/**
 * A single collapsible section for use as a child of `Accordion`. Inherits the
 * parent `Accordion`'s `variant` via context unless overridden. Header content
 * is composed as an optional line above the title, then the legacy icon on
 * the title row, `beforeLabel` (inline, or a leading column when the title
 * is stacked), optional `title`, `afterLabel`, then an optional line below
 * the title. `supportingContent` stays visible between the trigger and the
 * collapsible `children` body. `trailingContent` stays visible after that
 * body. `children` is a real body slot, making the item DSL-authorable.
 */
export const AccordionItem = React.forwardRef<
  React.ElementRef<typeof ShadcnAccordionItem>,
  AccordionItemProps
>(
  (
    {
      title,
      children,
      icon,
      iconBg,
      beforeLabel,
      afterLabel,
      aboveTitle,
      belowTitle,
      supportingContent,
      trailingContent,
      value,
      variant: variantProp,
      density: densityProp,
      titleSize: titleSizeProp,
      titleWeight: titleWeightProp,
      iconSize: iconSizeProp,
      chevronSize: chevronSizeProp,
      radius: radiusProp,
      triggerPosition: triggerPositionProp,
      triggerPaddingX: triggerPaddingXProp,
      triggerPaddingY: triggerPaddingYProp,
      contentPaddingX: contentPaddingXProp,
      contentPaddingY: contentPaddingYProp,
      labelGap: labelGapProp,
    },
    ref,
  ) => {
    const contextVariant = React.useContext(AccordionVariantContext);
    const config = React.useContext(AccordionConfigContext);
    const variant = variantProp ?? contextVariant ?? 'no-background';

    // Per-item prop wins, then the parent Accordion's value, then the
    // current-behavior default.
    const density = densityProp ?? config?.density ?? 'default';
    const titleSize = titleSizeProp ?? config?.titleSize ?? 'h5';
    const titleWeight = titleWeightProp ?? config?.titleWeight ?? 'bold';
    const iconSize = iconSizeProp ?? config?.iconSize ?? 'sm';
    // Compact density also tightens the default chevron (xl → lg) so the
    // collapsed trigger reaches the dense ~48px reference; explicit sizes win.
    const chevronSizeOption =
      chevronSizeProp ??
      config?.chevronSize ??
      (density === 'compact' ? 'lg' : 'xl');
    const radius = radiusProp ?? config?.radius ?? 'sm';
    const chevronSize: AccordionChevronSize =
      chevronSizeOption === 'follow-title'
        ? FOLLOW_TITLE_CHEVRON[titleSize]
        : chevronSizeOption;
    const triggerPosition =
      triggerPositionProp ?? config?.triggerPosition ?? 'spread';
    const triggerPaddingX = triggerPaddingXProp ?? config?.triggerPaddingX;
    const triggerPaddingY = triggerPaddingYProp ?? config?.triggerPaddingY;
    const contentPaddingX = contentPaddingXProp ?? config?.contentPaddingX;
    const contentPaddingY = contentPaddingYProp ?? config?.contentPaddingY;
    const labelGap = labelGapProp ?? config?.labelGap ?? 'xs';

    const generatedValue = React.useId();
    const itemValue = value ?? generatedValue;
    const showIconBg = iconBg ?? true;
    const iconBackground =
      variant === 'primary'
        ? 'primary'
        : variant === 'secondary'
          ? 'secondary'
          : 'neutral';
    const inlineBeforeLabel = asInlineTriggerContent(beforeLabel);
    const inlineAfterLabel = asInlineTriggerContent(afterLabel);
    const inlineAboveTitle = asInlineTriggerContent(aboveTitle);
    const inlineBelowTitle = asInlineTriggerContent(belowTitle);
    const accessibleName = [
      collectTriggerText(inlineAboveTitle),
      collectTriggerText(inlineBeforeLabel),
      title,
      collectTriggerText(inlineAfterLabel),
      collectTriggerText(inlineBelowTitle),
    ]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ');

    if (!accessibleName) {
      warnOnce(
        'AccordionItem/missing-trigger-name',
        '[hen-ui/AccordionItem] Trigger has no accessible name. Provide `title` or visible text in `beforeLabel` / `afterLabel`.',
      );
    }

    const labelColor = variant === 'onDark' ? 'white' : undefined;
    const labelLayoutClassName = cn(
      'flex min-w-0 flex-wrap items-center',
      SPACE_GAP[labelGap],
    );
    const stackedTitle = Boolean(aboveTitle || belowTitle);
    const labelContent = (
      <>
        {icon && (
          <Icon
            icon={icon}
            size={iconSize}
            color='black'
            background={showIconBg ? iconBackground : 'default'}
            reserveBackground
            aria-hidden
          />
        )}
        {stackedTitle ? null : inlineBeforeLabel}
        {title}
        {inlineAfterLabel}
      </>
    );

    // An accordion title is always a semantic heading (`Heading` renders the
    // `<hX>` role via the trigger). Size and weight are configurable; heading
    // semantics are not.
    const label = (
      <Heading
        variant={titleSize}
        asChild
        color={labelColor}
        className={cn(labelLayoutClassName, TITLE_WEIGHT_CLASS[titleWeight])}
      >
        {/* Plain span, not a nested `Text`: `Text`'s inline-copy
            `font-size: inherit` would override the `text-h{n}` size class. */}
        <span>{labelContent}</span>
      </Heading>
    );
    // `flex-1` stretches the stack to fill the row, which holds the title on
    // the left regardless of the trigger's `justify-*`. Grouped positions
    // (`end` / `start`) need the stack to shrink to its content instead, or
    // the setting would be a silent no-op for stacked titles. Any other value
    // keeps the full-width stack, matching the trigger's `spread` fallback.
    const isGroupedTriggerPosition =
      triggerPosition === 'end' || triggerPosition === 'start';
    const stackedWidthClassName = isGroupedTriggerPosition
      ? 'min-w-0'
      : 'min-w-0 flex-1';
    const titleStack = (
      <Flex direction='column' gap='none' className={stackedWidthClassName}>
        {inlineAboveTitle}
        {label}
        {inlineBelowTitle}
      </Flex>
    );
    const triggerLabel = stackedTitle ? (
      beforeLabel ? (
        <Flex
          direction='row'
          align='center'
          gap={labelGap}
          className={stackedWidthClassName}
        >
          {inlineBeforeLabel}
          {titleStack}
        </Flex>
      ) : (
        titleStack
      )
    ) : (
      label
    );

    return (
      <ShadcnAccordionItem
        ref={ref}
        value={itemValue}
        className={cn(accordionVariants({ variant, radius }))}
      >
        <ShadcnAccordionTrigger
          data-hen-id={COMPONENT_IDS.AccordionItem}
          density={density}
          chevronSize={chevronSize}
          triggerPosition={triggerPosition}
          paddingX={triggerPaddingX}
          paddingY={triggerPaddingY}
          headingTag='h3'
        >
          {triggerLabel}
        </ShadcnAccordionTrigger>
        {insetRegion(supportingContent, contentPaddingX)}
        <ShadcnAccordionContent
          paddingX={contentPaddingX}
          paddingY={contentPaddingY}
        >
          {children}
        </ShadcnAccordionContent>
        {insetRegion(trailingContent, contentPaddingX)}
      </ShadcnAccordionItem>
    );
  },
);

AccordionItem.displayName = 'AccordionItem';

export const Accordion = React.forwardRef<
  React.ElementRef<typeof ShadcnAccordion>,
  AccordionProps
>((props: AccordionProps, ref) => {
  const {
    variant,
    density,
    titleSize,
    titleWeight,
    iconSize,
    chevronSize,
    radius,
    triggerPosition,
    triggerPaddingX,
    triggerPaddingY,
    contentPaddingX,
    contentPaddingY,
    labelGap,
    anchorId,
  } = props;
  const anchor = resolveAnchorId(anchorId, undefined);
  const accordionProps = resolveAccordionRootProps(props);

  // Accordion-level presentation knobs, inherited by every item through
  // context (per-item props still override).
  const styleConfig: AccordionStyleProps = {
    density,
    titleSize,
    titleWeight,
    iconSize,
    chevronSize,
    radius,
    triggerPosition,
    triggerPaddingX,
    triggerPaddingY,
    contentPaddingX,
    contentPaddingY,
    labelGap,
  };

  // The programmatic `items` API renders through `AccordionItem` too, so the
  // trigger/body markup stays in one place. Per-item style props fall back to
  // the Accordion-level values via context when unset.
  const content = isItemsMode(props)
    ? props.items.map((item, index) => (
        <AccordionItem
          key={index}
          title={item.title}
          icon={item.icon}
          iconBg={item.iconBg}
          beforeLabel={item.beforeLabel}
          afterLabel={item.afterLabel}
          aboveTitle={item.aboveTitle}
          belowTitle={item.belowTitle}
          supportingContent={item.supportingContent}
          trailingContent={item.trailingContent}
          value={item.value ?? `item-${index}`}
          density={item.density}
          titleSize={item.titleSize}
          titleWeight={item.titleWeight}
          iconSize={item.iconSize}
          chevronSize={item.chevronSize}
          radius={item.radius}
          triggerPosition={item.triggerPosition}
          triggerPaddingX={item.triggerPaddingX}
          triggerPaddingY={item.triggerPaddingY}
          contentPaddingX={item.contentPaddingX}
          contentPaddingY={item.contentPaddingY}
          labelGap={item.labelGap}
        >
          {item.content}
        </AccordionItem>
      ))
    : props.children;

  return (
    <AccordionVariantContext.Provider value={variant}>
      <AccordionConfigContext.Provider value={styleConfig}>
        <ShadcnAccordion
          ref={ref}
          data-hen-id={COMPONENT_IDS.Accordion}
          {...accordionProps}
          {...(anchor ? { id: anchor } : {})}
        >
          {content}
        </ShadcnAccordion>
      </AccordionConfigContext.Provider>
    </AccordionVariantContext.Provider>
  );
});

Accordion.displayName = 'Accordion';
